'use client';

import { z } from 'zod';

// Common validation schemas
export const emailSchema = z.object({
  email: z.string().email('Invalid email address'),
});

export const passwordSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

export const nameSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

// Combined schemas
export const userRegistrationSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  roles: z.array(z.enum(['VETERINARIAN', 'INTERPRETER', 'ADMIN'])),
});

export const userLoginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const userProfileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
});

// File upload validation schemas
export const fileUploadSchema = z.object({
  files: z.array(z.instanceof(File)).min(1, 'At least one file is required'),
});

export const singleFileUploadSchema = z.object({
  file: z.instanceof(File, { message: 'Please select a file' }),
});

export const multipleFileUploadSchema = z.object({
  files: z
    .array(z.instanceof(File))
    .min(1, 'At least one file is required')
    .max(10, 'Maximum 10 files allowed'),
});

export const imageUploadSchema = z.object({
  files: z
    .array(z.instanceof(File))
    .min(1, 'At least one image is required')
    .refine(
      (files) => files.every((file) => file.type.startsWith('image/')),
      'All files must be images'
    ),
});

export const documentUploadSchema = z.object({
  files: z
    .array(z.instanceof(File))
    .min(1, 'At least one document is required')
    .refine(
      (files) =>
        files.every(
          (file) =>
            file.type === 'application/pdf' ||
            file.type === 'application/msword' ||
            file.type ===
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
            file.type === 'text/plain'
        ),
      'Only PDF, Word documents, and text files are allowed'
    ),
});

export const fileSizeSchema = (maxSizeInMB: number) =>
  z.object({
    files: z
      .array(z.instanceof(File))
      .refine(
        (files) =>
          files.every((file) => file.size <= maxSizeInMB * 1024 * 1024),
        `File size must be less than ${maxSizeInMB}MB`
      ),
  });

// Generic form schemas
export const createFormSchema = <T extends z.ZodRawShape>(schema: T) =>
  z.object(schema);

// Export types
export type EmailFormData = z.infer<typeof emailSchema>;
export type PasswordFormData = z.infer<typeof passwordSchema>;
export type NameFormData = z.infer<typeof nameSchema>;
export type UserRegistrationData = z.infer<typeof userRegistrationSchema>;
export type UserLoginData = z.infer<typeof userLoginSchema>;
export type UserProfileData = z.infer<typeof userProfileSchema>;
export type FileUploadData = z.infer<typeof fileUploadSchema>;
export type SingleFileUploadData = z.infer<typeof singleFileUploadSchema>;
export type MultipleFileUploadData = z.infer<typeof multipleFileUploadSchema>;
export type ImageUploadData = z.infer<typeof imageUploadSchema>;
export type DocumentUploadData = z.infer<typeof documentUploadSchema>;
